import { Box, IconButton, Typography, Skeleton, Button } from '@mui/material';
import { DownloadSimple, TrashSimple } from '@phosphor-icons/react';
import theme from '../../theme';
import { useQuery } from '@tanstack/react-query';
import {
  getKnowledgeBaseFiles,
  deleteKnowledgeBaseFile,
} from '../../utils/apis';
import { FC, useEffect, useState } from 'react';
import { IKnowledgeBaseFiles } from '../../schemas/apis/interface';
import DeleteModal from '../modals/DeleteModal';
import { useAlert } from '../../hooks/useAlert';
import MessagePopup from '../common/MessagePopup';
import UploadKnowledgeBase from './UploadKnowledgeBase';
import { useUser } from '../../hooks/useUser';
import NoDataFallback from '../common/NoDataFallback';

interface KnowledgeBaseFilesProps {
  setIsModalOpen?: (isOpen: boolean) => void;
  borderRadius?: string;
  border?: string;
  selectedStoreId: string;
  isStoreLoading: boolean;
  isGeneralSupport?: boolean;
}

const KnowledgeBAseFiles: FC<KnowledgeBaseFilesProps> = ({
  borderRadius = '',
  border = 'none',
  setIsModalOpen,
  selectedStoreId,
  isStoreLoading,
  isGeneralSupport = false,
}) => {
  const { alert, setAlert, showAlert } = useAlert();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);
  const [isPending, setIsPending] = useState<boolean>(false);
  const [knowledgeBaseFiles, setKnowledgeBaseFiles] =
    useState<IKnowledgeBaseFiles>();
  const { user } = useUser();

  const {
    data: filesData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['get-knowledge-base-files', selectedStoreId],
    queryFn: () => getKnowledgeBaseFiles(selectedStoreId || ''),
    enabled: !!selectedStoreId,
    refetchOnWindowFocus: false,
    retry: 0,
    refetchOnMount: false,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
  });

  useEffect(() => {
    if (filesData) {
      setKnowledgeBaseFiles(filesData.data);
    }
  }, [filesData]);

  const handleDownload = (url: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteClick = (fileName: string) => {
    setFileToDelete(fileName);
    setModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (fileToDelete) {
      setIsPending(true);
      try {
        await deleteKnowledgeBaseFile(
          user?._id || '',
          selectedStoreId.toString(),
          fileToDelete
        );
        showAlert('File deleted successfully', 'success');
        refetch();
      } catch (error) {
        console.error('Failed to delete file:', error);
        showAlert('Failed to delete file. Please try again.', 'error');
      } finally {
        setIsPending(false);
        setModalOpen(false);
        setFileToDelete(null);
      }
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        backgroundColor: '#FFFFFF',
        height: '100%',
        width: '100%',
        p: '24px',
        cursor: 'context-menu',
        overflowY: 'scroll',
        scrollbarWidth: 'none',
        borderRadius: borderRadius,
        border: border,
      }}
    >
      {isLoading || isStoreLoading ? (
        // Loading Skeleton
        [...Array(6)].map((_, index) => (
          <Box
            key={index}
            sx={{
              height: '88px',
              borderRadius: '8px',
              padding: '24px',
              backgroundColor: '#FFFFFF',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              border: '1px solid #DCE0E7',
              width: '100%',
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Skeleton variant="text" width="60%" height={24} />
              <Skeleton variant="text" width="40%" height={20} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Skeleton variant="circular" width={40} height={40} />
              <Skeleton variant="circular" width={40} height={40} />
            </Box>
          </Box>
        ))
      ) : knowledgeBaseFiles?.files?.length === 0 ? (
        !isGeneralSupport ? (
          <UploadKnowledgeBase setIsModalOpen={setIsModalOpen} />
        ) : (
          <NoDataFallback
            message="No Uploaded Files"
            description="Currently no files are available in knowledge base. Please upload a file to get started."
          />
        )
      ) : (
        // Render files
        knowledgeBaseFiles?.files?.map((file, index) => (
          <Box
            key={index}
            sx={{
              height: '88px',
              borderRadius: '8px',
              padding: '24px',
              backgroundColor: '#FFFFFF',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              border: '1px solid #DCE0E7',
              width: '100%',
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: theme.palette.custom.textBlue,
                }}
              >
                {file.fileName}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
              }}
            >
              <Button
                startIcon={<DownloadSimple size="16" />}
                color="primary"
                sx={{
                  fontWeight: 500,
                  borderRadius: '6px',
                  fontSize: '14px',
                  textTransform: 'none',
                }}
                onClick={() => handleDownload(file.url, file.fileName)}
              >
                Download File
              </Button>
              {!isGeneralSupport && (
                <IconButton
                  sx={{
                    backgroundColor: '#E74C3C26',
                    borderRadius: '8px',
                  }}
                  onClick={() => handleDeleteClick(file.fileName)}
                >
                  <TrashSimple
                    size={20}
                    color={theme.palette.error.main}
                    weight="fill"
                  />
                </IconButton>
              )}
            </Box>
          </Box>
        ))
      )}

      <MessagePopup alert={alert} setAlert={setAlert} />

      {/* Delete Modal */}
      <DeleteModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onDelete={() => void handleConfirmDelete()}
        isPending={isPending}
        title="Delete File"
        description="Are you sure this action cannot be undone and will permanently delete the uploaded file"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Box>
  );
};

export default KnowledgeBAseFiles;
